import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateProfile } from '../../store/slices/authSlice';
import {
  Box, Typography, TextField, Button, Card, CardContent, Grid,
  CircularProgress, Alert, Chip
} from '@mui/material';
import { AdminPanelSettings, Save } from '@mui/icons-material';
import api from '../../services/api';

const AdminProfile = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    email: user?.email || '', phone: user?.phone || ''
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setSaving(true);
    const result = await dispatch(updateProfile(formData));
    if (updateProfile.fulfilled.match(result)) {
      setSuccess('Profile updated successfully. Use your new email next time you log in.');
    } else {
      setError(result.payload || 'Failed to update profile.');
    }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    try {
      await api.put('/auth/updatepassword', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password changed successfully.');
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Failed to change password.');
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Admin Profile</Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
        <AdminPanelSettings color="primary" />
        <Typography variant="body1">{user?.firstName} {user?.lastName}</Typography>
        <Chip label="admin" size="small" color="error" />
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Account Details</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This email is your login username — changing it here changes what you sign in with.
          </Typography>
          <Box component="form" onSubmit={handleProfileUpdate}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name" value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Login Email" type="email" value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </Grid>
            </Grid>
            <Button type="submit" variant="contained" startIcon={<Save />} disabled={saving} sx={{ mt: 2 }}>
              {saving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>Change Password</Typography>
          {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
          <Box component="form" onSubmit={handlePasswordChange}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label="Current Password" type="password" value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="New Password" type="password" value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Confirm Password" type="password" value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })} />
              </Grid>
            </Grid>
            <Button type="submit" variant="outlined" sx={{ mt: 2 }}>Change Password</Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminProfile;
